import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import TechnicianPicker from "./TechnicianPicker";
import { useSetTechniciansMutation } from "../technicalApi";

// Replaces the FULL assigned-technician set for a work item - the API
// diff-applies it server-side, so the list posted here is the list that ends
// up assigned. Pre-filled with whoever is currently on the job for exactly
// that reason: submitting a fresh empty selection would unassign everyone.
//
// For a Complaint or Service Visit, assigning the first technician also flips
// the status NOT_STARTED -> ASSIGNED and skips the separate Start step
// entirely. An Installation never reaches ASSIGNED and still needs Start.
const AssignTechniciansModal = ({ show, onClose, work, technicians = [] }) => {
  const [setTechnicians, { isLoading }] = useSetTechniciansMutation();
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!show) return;
    setSelected(
      technicians.map((t) => ({
        value: t.employeeRecordGuid,
        label: `${t.employeeFirstName || ""} ${t.employeeLastName || ""}`.trim() + ` · ${t.employeeCode}`,
      }))
    );
    // technicians is a fresh array each render; keying off the modal opening
    // and the work item is what actually matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, work?.recordGuid]);

  const handleSave = async () => {
    try {
      await setTechnicians({
        recordGuid: work.recordGuid,
        technicianEmployeeRecordGuids: selected.map((s) => s.value),
      }).unwrap();
      toast.success("Technicians updated");
      onClose();
    } catch (e) {
      toast.error(e?.message || "Could not update the technicians");
    }
  };

  const isUnassigningAll = selected.length === 0 && technicians.length > 0;

  return (
    <Modal show={show} onHide={isLoading ? undefined : onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Assign Technicians</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          {work?.workNumber} &middot; {work?.customerName} &middot; {work?.branchCode}
        </p>
        <TechnicianPicker
          branchId={work?.branchId}
          value={selected}
          onChange={(v) => setSelected(v || [])}
          menuPortalTarget={document.body}
        />
        {isUnassigningAll && (
          <div className="alert alert-warning mt-3 mb-0 py-2 px-3">
            <small>This removes every technician from the job, leaving it unassigned.</small>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={isLoading}>
          {isLoading ? "Saving..." : "Save Assignment"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignTechniciansModal;
